import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Listings } from '../api/broker';
import {
    CertImage,
    PsaCertLink,
    PriceChartingGradesCell,
    PriceChartingProductCell,
    formatMoney,
    certMatchesQuery,
} from '../components/CertCells';
import { useToast } from '../components/Toast';
import AutoPriceInfo from '../components/AutoPriceInfo';

const ListingsTab = ({ brokerEmail, filters, goTo, onZoom, onCountChange }) => {
    const toast = useToast();
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rowPrices, setRowPrices] = useState({});
    const [savedPrices, setSavedPrices] = useState({});
    const [rowAutoPrice, setRowAutoPrice] = useState({});
    const [busyRows, setBusyRows] = useState({});
    const [query, setQuery] = useState(filters.q || '');

    useEffect(() => { setQuery(filters.q || ''); }, [filters.q]);

    const setRowBusy = (cert_number, v) =>
        setBusyRows((prev) => ({ ...prev, [cert_number]: v }));

    const removeRow = (cert_number) =>
        setListings((prev) => prev.filter((l) => l.cert?.cert_number !== cert_number));

    const load = useCallback(async () => {
        if (!brokerEmail) {
            setListings([]);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const result = await Listings.listForSeller({ sellerEmail: brokerEmail });
            const visible = result.listings || [];
            setListings(visible);
            onCountChange?.(visible.length);

            const prices = {};
            const autoPrice = {};
            visible.forEach((l) => {
                if (l.cert?.cert_number != null) {
                    prices[l.cert.cert_number] = l.price !== undefined && l.price !== null
                        ? Number(l.price).toFixed(2)
                        : '';
                    autoPrice[l.cert.cert_number] = !!l.auto_price;
                }
            });
            setRowPrices(prices);
            setSavedPrices(prices);
            setRowAutoPrice(autoPrice);
        } catch (e) {
            setError(e.message);
            setListings([]);
            onCountChange?.(0);
        } finally {
            setLoading(false);
        }
    }, [brokerEmail, onCountChange]);

    useEffect(() => { load(); }, [load]);

    const onPriceChange = (cert_number, price) =>
        setRowPrices((prev) => ({ ...prev, [cert_number]: price }));

    // Auto-price saves the moment it's toggled; it flips back if the save fails.
    const handleAutoPriceChange = async (listing, autoPrice) => {
        const certNumber = listing.cert?.cert_number;
        setRowAutoPrice((prev) => ({ ...prev, [certNumber]: autoPrice }));
        setRowBusy(certNumber, true);
        try {
            await Listings.update({ listingId: listing.listing_id, autoPrice });
            toast.success(`Auto-price ${autoPrice ? 'on' : 'off'} for #${certNumber}`);
        } catch (e) {
            setRowAutoPrice((prev) => ({ ...prev, [certNumber]: !autoPrice }));
            toast.error(`Failed to update #${certNumber}: ${e.message}`);
        } finally {
            setRowBusy(certNumber, false);
        }
    };

    const handleUpdate = async (listing) => {
        const certNumber = listing.cert?.cert_number;
        const price = rowPrices[certNumber];
        if (!price || Number.isNaN(parseFloat(price))) {
            toast.error(`Enter a valid price first`);
            return;
        }
        setRowBusy(certNumber, true);
        try {
            await Listings.update({ listingId: listing.listing_id, price });
            setSavedPrices((prev) => ({ ...prev, [certNumber]: price }));
            toast.success(`Updated listing for #${certNumber} to ${formatMoney(price)}`);
        } catch (e) {
            toast.error(`Failed to update #${certNumber}: ${e.message}`);
        } finally {
            setRowBusy(certNumber, false);
        }
    };

    const handleRemove = async (listing) => {
        const certNumber = listing.cert?.cert_number;
        if (!window.confirm(`Remove the listing for #${certNumber}?`)) return;
        setRowBusy(certNumber, true);
        try {
            await Listings.deactivate(certNumber);
            toast.success(`Removed listing for #${certNumber}`);
            removeRow(certNumber);
        } catch (e) {
            toast.error(`Failed to remove #${certNumber}: ${e.message}`);
            setRowBusy(certNumber, false);
        }
    };

    const visible = useMemo(
        () => listings.filter((l) => certMatchesQuery(l.cert, query)),
        [listings, query]
    );

    return (
        <div className="broker-tab-panel">
            <div className="broker-toolbar">
                <input
                    placeholder="Filter by name / cert # / set…"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        goTo({
                            tab: 'listings',
                            q: e.target.value || undefined,
                        });
                    }}
                />
                <span className="broker-toolbar-spacer" />
                <span className="broker-stat">
                    {visible.length} listing{visible.length === 1 ? '' : 's'}
                </span>
            </div>

            {error && <p className="broker-error">{error}</p>}
            {loading && <p className="broker-loading">Loading listings…</p>}

            {!loading && !error && (
                visible.length === 0 ? (
                    <p className="broker-empty">No active listings yet.</p>
                ) : (
                    <div className="broker-table-wrap">
                        <table className="broker-table">
                            <thead>
                                <tr>
                                    <th className="col-image"></th>
                                    <th>Cert #</th>
                                    <th>Name</th>
                                    <th className="col-grade">Grade</th>
                                    <th>Set / #</th>
                                    <th className="col-price">PriceCharting</th>
                                    <th>Product</th>
                                    <th className="col-price-input">Price</th>
                                    <th className="col-auto-price" title="Reprice automatically when market pricing updates">Auto<AutoPriceInfo /></th>
                                    <th className="col-actions"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map((listing) => {
                                    const cert = listing.cert || {};
                                    const certNumber = cert.cert_number;
                                    const busy = !!busyRows[certNumber];
                                    const priceChanged = (rowPrices[certNumber] ?? '') !== (savedPrices[certNumber] ?? '');
                                    return (
                                        <tr key={listing.id} className={busy ? 'busy' : ''}>
                                            <td className="col-image">
                                                <CertImage cert={cert} onZoom={onZoom} listingHref={certNumber ? `/listing/${certNumber}` : null} />
                                            </td>
                                            <td className="cell-cert-number"><PsaCertLink certNumber={certNumber} /></td>
                                            <td>{cert.name || '—'}</td>
                                            <td className="col-grade">{cert.grade ? cert.grade.match(/\d+/)?.[0] : '—'}</td>
                                            <td>
                                                <div className="cell-set">{cert.card?.set?.name || '—'}</div>
                                                <div className="cell-set-num muted">#{cert.set_number || '—'}</div>
                                            </td>
                                            <td><PriceChartingGradesCell cert={cert} /></td>
                                            <td><PriceChartingProductCell cert={cert} /></td>
                                            <td className="col-price-input">
                                                <div className="price-input-stack">
                                                    <div className="price-input-wrap">
                                                        <span className="price-input-prefix">$</span>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            className="price-input"
                                                            value={rowPrices[certNumber] ?? ''}
                                                            onChange={(e) => onPriceChange(certNumber, e.target.value)}
                                                            onKeyDown={(e) => e.key === 'Enter' && priceChanged && !busy && handleUpdate(listing)}
                                                            disabled={busy}
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="broker-btn-primary broker-btn-sm price-update-btn"
                                                        disabled={busy || !priceChanged}
                                                        onClick={() => handleUpdate(listing)}
                                                    >
                                                        Update
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="col-auto-price">
                                                <input
                                                    type="checkbox"
                                                    aria-label={`Auto-price #${certNumber}`}
                                                    checked={!!rowAutoPrice[certNumber]}
                                                    onChange={(e) => handleAutoPriceChange(listing, e.target.checked)}
                                                    disabled={busy}
                                                />
                                            </td>
                                            <td className="col-actions">
                                                <button
                                                    type="button"
                                                    className="broker-btn-ghost broker-btn-sm"
                                                    disabled={busy}
                                                    onClick={() => handleRemove(listing)}
                                                >
                                                    Remove
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )
            )}
        </div>
    );
};

export default ListingsTab;
